import type { ScaleType } from '@tunasong/schemas'
import type { ChordFunction } from './types.js'

/**
 * Curated library of named chord progressions. Each entry's `pattern` is a
 * sequence of `ChordFunction` Roman numerals (matching `getDiatonicFunction`
 * output) and `id` resolves to a lexicon entry id — `lexicon.ts` owns the
 * user-facing copy, this table owns the match shape.
 *
 * Phase-1 starter set (Bowie-curated, transcribed from the Notion task at
 * https://www.notion.so/3628e623975c810696ece4a9ab64c3ce):
 *   - I–V–vi–IV  ("Four-chord pop loop" / Axis-of-Awesome)
 *   - vi–IV–I–V  ("Sensitive-female / minor-start pop")
 *   - I–vi–IV–V  ("50s doo-wop / ice-cream changes")
 *   - ii–V–I     ("Jazz turnaround")
 *
 * Phase-1.5 minor-key set (unblocked by Quincy's V-in-minor fix in PR #1343,
 * which made `getDiatonicFunction` return `V` for the major V chord in
 * natural minor):
 *   - i–iv–V–i           ("Minor cadence" — conventional minor authentic)
 *   - i–♭VII–♭VI–V       ("Andalusian cadence" — Spanish chromatic descent)
 *   - i–♭VI–♭III–♭VII    ("Minor pop loop" — modern rock/pop)
 *   - i–V–♭VI–iv         ("Modern minor pop" — V→♭VI deceptive lift)
 *
 * `expectedScaleType` gates the match: a progression named in terms of major-
 * key functions can only match when the working key is major. Minor-key
 * progressions match `'minor'` only; modal progressions (e.g., Mixolydian
 * I–♭VII–IV) remain deferred to Phase 2.
 *
 * Matching contract (handled by `progression-match.ts`):
 *   - Function-blind (transposition-blind): match on Roman numerals not roots.
 *   - Cyclic rotation tolerated: `V–vi–IV–I` matches `I–V–vi–IV`.
 *   - Direct-match preferred over rotated-match (deterministic tie-break).
 *   - Minimum window length = 3 — no 2-chord fragment matches.
 */
export interface ProgressionLibraryEntry {
  /** Stable id — must match a `LexiconEntry.id`. */
  id: string
  /** Function-Roman pattern in the entry's order. */
  pattern: ChordFunction[]
  /** Scale family the pattern is *expected* to belong to. Match fails if the
   *  working scale's `type` is not in this list. */
  expectedScaleType: ScaleType[]
}

export const PROGRESSION_LIBRARY: ProgressionLibraryEntry[] = [
  {
    id: 'progression-I-V-vi-IV',
    pattern: ['I', 'V', 'vi', 'IV'],
    expectedScaleType: ['major'],
  },
  {
    id: 'progression-vi-IV-I-V',
    pattern: ['vi', 'IV', 'I', 'V'],
    expectedScaleType: ['major'],
  },
  {
    id: 'progression-I-vi-IV-V',
    pattern: ['I', 'vi', 'IV', 'V'],
    expectedScaleType: ['major'],
  },
  {
    id: 'progression-ii-V-I',
    pattern: ['ii', 'V', 'I'],
    expectedScaleType: ['major'],
  },
  // ───────── Minor-key (Phase 1.5) ─────────
  {
    id: 'progression-i-iv-V-i',
    pattern: ['i', 'iv', 'V', 'i'],
    expectedScaleType: ['minor'],
  },
  {
    id: 'progression-i-flat-VII-flat-VI-V',
    pattern: ['i', '♭VII', '♭VI', 'V'],
    expectedScaleType: ['minor'],
  },
  {
    id: 'progression-i-flat-VI-flat-III-flat-VII',
    pattern: ['i', '♭VI', '♭III', '♭VII'],
    expectedScaleType: ['minor'],
  },
  {
    id: 'progression-i-V-flat-VI-iv',
    pattern: ['i', 'V', '♭VI', 'iv'],
    expectedScaleType: ['minor'],
  },
]
